<template>
    <div style="position: relative;">
        <button v-show="shouldReset"
            type="button"
            class="btn btn-success" style="position:absolute; top:50px; right:140px; z-index: 10;"
            @click="resetGraph()">
            {{ $t("Reset") }}
        </button>
        <canvas :id="props.chartId" :width=props.canvasWidth :height="computedCanvasHeight"/>
    </div>
</template>

<script>
export const AppChartProps = {
    chartId: {
        type: String,
        default: 'chart',
    },
    type: {
        type: String,
        required: true,
    },
    data: {
        type: Object,
        required: true,
    },
    options: {
        type: Object,
        required: true,
    },
    plugins: {
        type: Array,
        default: () => [],
    },
    canvasWidth: {
        type: Number,
        default: 1000,
    },
    canvasHeight: {
        type: Number,
        default: ({ canvasWidth }) => window.innerWidth > 1290 ?  canvasWidth / 3 : canvasWidth / 2,
    },
    forceCanvasDimesions: {
        type: Boolean,
        default: false,
    },
    scale: {
        type: Object
    }
}
</script>

<script setup>
import {onMounted, onBeforeUnmount, ref, watch, defineEmits, nextTick} from "vue";
import {trans as $t} from "@/vue/composables/missingTranslations";
const props = defineProps(AppChartProps)
const emits = defineEmits(['update:image', 'zoom']);
const shouldReset = ref(false);
let tooltipLabels = ref([]);
let chart = ref(null);
const computedCanvasHeight = ref(getComputedCanvasHeight());
function getComputedCanvasHeight() {
    if (props.forceCanvasDimesions) {
        return props.canvasHeight;
    }
    if (window.innerWidth > 1290) {
        return props.canvasWidth / 3;
    } else if (window.innerWidth > 800) {
        return props.canvasWidth / 2;
    } else {
        return props.canvasHeight;
    }
}

//watch(() => [props.canvasWidth], () => chart.setWidth(props.canvasWidth));
function data() {
    return {
        image: {},
    }
}

function buildChart() {
    return {
        type: props.type,
        data: props.data,
        options: buildOptions(),
        plugins: buildPlugins(),
    };
}

onMounted(() => {
    chart.value = new Chart(props.chartId, buildChart());
    window.addEventListener("resize", resizeHandler);
});
onBeforeUnmount(() => {
    window.removeEventListener("resize", resizeHandler);
});

function buildOptions() {
    const options = props.options;

    options.resizeDelay = 10;

    // if ('xAxis' in options.scales) { //TODO: check if this can be removed...
    //     let ticks = {}
    //     if (options.scales.xAxis.time.unit === 'day') {
    //         ticks = dailyTicks;
    //     } else if (options.scales.xAxis.time.unit === 'hour') {
    //         ticks = hourlyTicks;
    //     }
    //     options.scales.xAxis.ticks = {
    //         ...options.scales.xAxis?.ticks || {},
    //         ...ticks,
    //     };
    // }

    if (options.plugins?.tooltip) {
        const timeFormat = options.plugins.tooltip.timeFormat;
        options.plugins.tooltip = buildTooltips(timeFormat);
    }
    if (options.plugins?.zoom) {
        options.plugins.zoom.zoom.onZoomComplete = function (event) {
            emits("zoom", {
                min: event.chart.scales.x?.min,
                max: event.chart.scales.x?.max,
                trigger: props.chartId
            });
            shouldReset.value = true;
        };
    }

    return options;
}

function buildTooltips(timeFormat){
    return {
        reverse: true,
        callbacks: {
            label: (context) => {
                const label = context.dataset.label || '';
                const value = context.parsed.y || '0';

                if (label === "Mouthpiece") {
                    return '';
                }

                if(tooltipLabels.indexOf(label) !== -1){
                    return '';
                }
                tooltipLabels.push(label);
                let unit = '';
                if (label === "EPAP" || label === "IPAP") {
                    unit = " cmH2O";
                } else if (label === "Leak Level") {
                    unit = " L/min";
                }
                return label + ': ' + value + unit;
            },
            beforeTitle: function(context) {
                tooltipLabels = [];
            },
            afterFooter: function(context) {
                tooltipLabels = [];
            },
            title: function(context) {
                const title = context[0]?.label || '';
                if (title) {
                    return new Date(title).toLocaleString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                        hour: "numeric",
                        minute: "numeric",
                        hour12: timeFormat !== 24
                    });
                }
            },
        },
    };
}
const dailyTicks = {
    callback(value, index, ticks) {
        const date = moment(ticks[index].value);

        if (ticks.length < 16) {
            return [date.format('ddd '), date.format('MM/DD')];
        }
        if (date.format('dddd') === 'Monday') {
            return [date.format('ddd '), date.format('MM/DD')];
        }
        return null;
    }
};
const hourlyTicks = {
    callback(value, index, ticks) {
        const date = moment(ticks[index].value);

        if (date.hour() === 0) {
            return [date.format('MMM DD'), date.format('h:mm')];
        }
        if (ticks.length < 8) {
            return [date.format('h:mm')];
        }
        if (ticks.length > 8 && date.hour() % 2 === 0) {
            return [date.format('h:mm')];
        }
        return null;
    }
};

function buildPlugins() {
    return [
        ...props.plugins,
        ...[
            {
                id: 'saveImage',
                afterRender(chartObj, args, pluginOptions) {
                    const image = chartObj.toBase64Image('image/jpeg', 1);
                    emits('update:image', image);
                },
            },
        ]
    ];
}
function resizeHandler() {
    if (chart.value) {
        computedCanvasHeight.value = getComputedCanvasHeight();
        chart.value.resize();
    }
}

function resetGraph(){
    if (chart.value && typeof chart.value.resetZoom === 'function') {
        chart.value.resetZoom();
    }
    shouldReset.value = false;
}

watch(() => [props.scale], () => {
    if (props.chartId === props.scale.trigger) {
        return;
    }
    if (chart.value) {
        chart.value.zoomScale('x', {
            min: props.scale.min,
            max: props.scale.max
        });
        shouldReset.value = chart.value.isZoomedOrPanned();
    }
});

</script>
